import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  NgZone,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { isNull, isUndefined } from '@models';
import { BreadcrumbsService } from './breadcrumbs.service';
import { AccessibleClickDirective } from '../directives/accessible-click.directive';
import { BreakpointObserverService, FocusService, NavigationService } from '@core';
import { debounceTime, fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SkeletonTemplateService } from '@skeleton-loader';

export type Navigatable<T> = {
  path: string | null;
  params?: Object;
} & T;

export type BreadcrumbItem = Navigatable<{ name: string }>;

@Component({
  selector: 'lib-breadcrumbs',
  imports: [CommonModule, AccessibleClickDirective],
  templateUrl: './breadcrumbs.component.html',
  styleUrl: './breadcrumbs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsComponent implements AfterViewInit {
  dumb = input(false);
  white = input(false);
  light = input(false);
  dumbLabel = input('');
  showFade = input(false);
  makeAllClickable = input(false);

  skeletonService = inject(SkeletonTemplateService);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService); // Inject NavigationService
  private readonly breadcrumbsService = inject(BreadcrumbsService);
  private readonly focusService = inject(FocusService);
  readonly isMobile = inject(BreakpointObserverService).isMobile;

  isLoading = this.breadcrumbsService.isLoading;
  segments = this.breadcrumbsService.config;
  fadeAnimation = signal(true);

  scrollBreadcrumb = viewChild('scrollBreadcrumb', { read: ElementRef });

  clickSegment(segment: BreadcrumbItem): void {
    if (isNull(segment.path) || isUndefined(segment.path)) return;

    // Check if this is an Apple-related navigation
    if (this.isAppleNavigation(segment)) {
      this.handleAppleNavigation(segment);
    } else {
      // Use original navigation logic for non-Apple segments
      this.router
        .navigate([segment.path], {
          queryParams: {
            data: JSON.stringify(
              (segment.params as any)?.data?.map((s: BreadcrumbItem) => ({ ...s, subCategories: [] })),
            ),
          },
        })
        .then(() => {
          this.focusService.focusHeaderAction();
          this.breadcrumbsService.set((segment.params as any)?.data ?? []);
        });
    }
  }

  private isAppleNavigation(segment: BreadcrumbItem): boolean {
    // Check if this is Apple brand store navigation
    if (segment.path?.includes('/brandstore/1032')) {
      return true;
    }

    // Check if this is Apple category navigation
    if (segment.path?.includes('/apple/category/')) {
      return true;
    }

    return false;
  }

  private handleAppleNavigation(segment: BreadcrumbItem): void {
    // Handle Apple brand store navigation
    if (segment.path?.includes('/brandstore/1032')) {
      this.navigationService.goToBrandStore(1032);
      this.focusService.focusHeaderAction();
      this.breadcrumbsService.set([]);
      return;
    }

    // Handle Apple category navigation - check if this is an Apple category page path
    if (segment.path?.includes('/apple/category/')) {
      const categoryId = this.extractCategoryIdFromPath(segment.path);
      if (categoryId) {
        this.navigationService.goToAppleCategoryPage(categoryId);
        this.focusService.focusHeaderAction();
        this.breadcrumbsService.set((segment.params as any)?.data ?? []);
      }
      return;
    }

    // Handle regular category navigation within Apple hierarchy
    if (segment.path?.includes('/category/')) {
      const categoryId = this.extractCategoryIdFromPath(segment.path);
      if (categoryId) {
        // Check if this is a top-level Apple category that should use Apple category page
        const segmentData = (segment.params as any)?.data?.[0];
        if (segmentData && this.isTopLevelAppleCategory(segmentData) && this.hasAppleCategoryPage(categoryId)) {
          this.navigationService.goToAppleCategoryPage(categoryId);
        } else {
          this.navigationService.goToCategoryPage(categoryId);
        }
        this.focusService.focusHeaderAction();
        this.breadcrumbsService.set((segment.params as any)?.data ?? []);
      }
      return;
    }
  }

  private isTopLevelAppleCategory(category: any): boolean {
    // Check if this category is a direct child of Apple (pId = 60001)
    return category.pId === 60001;
  }

  private hasAppleCategoryPage(categoryId: number): boolean {
    // Categories that have dedicated Apple category pages
    const APPLE_CATEGORY_PAGE_IDS = new Set([
      60002, // Mac
      60003, // iPad
      60004, // iPhone
      60005, // Watch
      60007, // AirPods
    ]);

    return APPLE_CATEGORY_PAGE_IDS.has(categoryId);
  }

  private extractCategoryIdFromPath(path: string): number | null {
    // Extract category ID from paths like '/apple/category/60004' or '/category/62001'
    const matches = path.match(/\/(?:apple\/)?category\/(\d+)/);
    return matches ? parseInt(matches[1], 10) : null;
  }

  isClickable(index: number): boolean {
    if (this.makeAllClickable()) return true;
    return index + 1 < this.segments().length;
  }

  goHome(): void {
    this.router.navigate(['']);
  }

  ngAfterViewInit(): void {
    if (!this.showFade()) return;
    this.zone.runOutsideAngular(() => {
      fromEvent(this.scrollBreadcrumb()?.nativeElement, 'scroll')
        .pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.zone.run(() => this.onBreadcrumbScroll());
        });
    });
  }

  onBreadcrumbScroll(): void {
    if (!this.scrollBreadcrumb()) return;
    const el = this.scrollBreadcrumb()?.nativeElement;
    const reachedEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

    this.fadeAnimation.set(!reachedEnd);
  }
}
